import React, { useEffect, useState } from "react";
import img from "../../assets/images/nr1.jpg";
import prestigeCampaignImg from "../../assets/images/prestige.png";
import pulseCampaignImg from "../../assets/images/pulse.png";
import { notification } from "antd";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import DataProvider from "../../services";
import Colors from "../../constants/Colors";
import images from "../../assets/images/poweredbymtnwhite.png";
import Steps from "../../components/elements/steps";
import UserRegister from "../../components/sections/user-register";
// import med from "../../assets/images/logo.png";
import SelectPlan from "../../components/sections/select-plan";
import AuthService from "../../services/auth";
import { REGISTRATION_PLATFORM_MCARE } from "../../constants/registrationPlatform";
import {
  convertToInternationalFormat,
  convertToLocalFormat,
} from "../../utils";
import { useAppStore } from "../../store";
import { resetProfileCompletionGate } from "../../lib/profile-completion";
import { trackSignup } from "../../utils/analytics";
import { v4 as uuidv4 } from "uuid";
import medpharmalogo from "../../assets/images/medpharma logo black.png";
import mcareblack from "../../assets/images/mcare logo (1) black.png";
import MTNLogoblack from "../../assets/images/mtnblack.png";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const CAMPAIGN_SOURCES = ["prestige", "pulse"] as const;
type CampaignSource = (typeof CAMPAIGN_SOURCES)[number];

function normalizeCampaignSource(raw: string | null): CampaignSource | null {
  const value = raw?.trim().toLowerCase();
  if (value === "prestige" || value === "pulse") return value;
  return null;
}

function campaignImageFor(source: CampaignSource) {
  return source === "prestige" ? prestigeCampaignImg : pulseCampaignImg;
}

type NotificationType = "success" | "info" | "warning" | "error";

export default function Register() {
  const query = useQuery();
  const location = useLocation();
  const campaignSource = normalizeCampaignSource(
    new URLSearchParams(location.search).get("source"),
  );
  const {
    setToken,
    setUser,
    setActivateData,
    activateData,
    setPlan,
    setPackage,
  } = useAppStore();
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [signupMethod, setSignupMethod] = useState<string | null>("email");
  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState<string>("");
  const [couponInput, setCouponInput] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [subscriptionMsisdn, setSubscriptionMsisdn] = useState<string>("");
  const [yelloCareCode, setYelloCareCode] = useState<string>("");
  const [prestige, setPrestige] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);

  async function tracking() {
    const source = query.get("source");
    const campaign = query.get("campaign");

    if (source && campaign) {
      // Store the data in local storage and backend.
      localStorage.setItem("@source", source);
      localStorage.setItem("@campaign", campaign);
      const payload = { source, campaign, trackingId: uuidv4() };
      const { data } = await DataProvider.createNewTracking(payload);
      if (data) {
        localStorage.setItem("@tracking", JSON.stringify(data));
      }
    }
  }

  // https://mcare.medpharma.care/sign-up?source=google&campaign=ads

  useEffect(() => {
    const source = localStorage.getItem("@source");
    const campaign = localStorage.getItem("@campaign");
    if (!source && !campaign) {
      tracking();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!campaignSource) return;

    const code = campaignSource.toUpperCase();
    setCouponInput(code);
    setAppliedCoupon(code);

    let cancelled = false;
    void (async () => {
      try {
        const result = await DataProvider.validateCoupon(code);
        if (!cancelled && !result.valid) {
          setAppliedCoupon(null);
        }
      } catch {
        // Keep the prefilled code; user can tap Apply to retry validation.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campaignSource]);

  const handleSignupMethodChange = (value: string) => {
    setSignupMethod(value);
  };

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string,
  ) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const hasCouponEnteredButNotApplied = (): boolean => {
    const entered = (couponInput ?? "").trim();
    const applied = (appliedCoupon ?? "").trim();
    return entered.length > 0 && applied.length === 0;
  };

  async function onSubmit() {
    if (name.length < 3) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Full name is required, type your full name!",
      );
    }

    if (hasCouponEnteredButNotApplied()) {
      return openNotificationWithIcon(
        "warning",
        "Coupon not applied",
        "You entered a coupon code but did not apply it. Please apply the coupon or clear the field to continue.",
      );
    }

    if (signupMethod === "email" && email.length < 3) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Email must not be empty, type your email address!",
      );
    }

    if (
      signupMethod === "phone" &&
      (!phoneNumber || phoneNumber.length !== 10)
    ) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Phone number must not be empty, type your phone number!",
      );
    }

    if (signupMethod === "phone") {
      if (!isOtpSent) {
        sendOtp();
      } else {
        verifyOtp();
      }
    }

    if (signupMethod === "email") {
      if (!isOtpSent) {
        sendEmailOtp();
      } else {
        verifyEmailOtp();
      }
    }
  }

  // Step 1 (email): validate + request the email OTP. The backend `/auth/signup`
  // no longer auto-logs-in — it only sends a verification code. The account is
  // created and tokens are issued in step 2 (verifyEmailOtp).
  const sendEmailOtp = async () => {
    if (appliedCoupon?.trim() && !subscriptionMsisdn?.trim()) {
      return openNotificationWithIcon(
        "warning",
        "Phone number required",
        "Phone number (msisdn) is required when applying a coupon during email signup.",
      );
    }

    if (!password) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Password must not be empty, type your password!",
      );
    }

    if (password.length < 8) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Password must be 8 or more characters!",
      );
    }

    setLoading(true);
    try {
      const emailLower = email.trim().toLowerCase();

      await AuthService.signUpEmail({
        email: emailLower,
        password,
        fullname: name,
        referralCode: referralCode.length > 3 ? referralCode : undefined,
        couponCode: appliedCoupon?.trim() || undefined,
        couponApplied: !!appliedCoupon?.trim(),
        registrationPlatform: REGISTRATION_PLATFORM_MCARE,
        msisdn:
          appliedCoupon?.trim() && subscriptionMsisdn?.trim()
            ? subscriptionMsisdn.trim()
            : undefined,
      });

      setIsOtpSent(true);
      setLoading(false);
      openNotificationWithIcon(
        "success",
        "OTP Sent",
        "Please check your email for the verification code.",
      );
    } catch (error: any) {
      trackSignup("email", false);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to send verification code. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  };

  // Step 2 (email): verify the OTP. This is where the account is created and
  // the auth tokens are issued, so the coupon/subscription request rides along
  // here and we only navigate once we hold a valid token.
  const verifyEmailOtp = async () => {
    if (otp.length < 6) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Please enter the 6-digit OTP!",
      );
    }
    if (hasCouponEnteredButNotApplied()) {
      return openNotificationWithIcon(
        "warning",
        "Coupon not applied",
        "You entered a coupon code but did not apply it. Please apply the coupon or clear the field to continue.",
      );
    }

    setLoading(true);
    try {
      const emailLower = email.trim().toLowerCase();

      const response = await AuthService.signUpEmailVerify({
        email: emailLower,
        password,
        fullname: name,
        otpCode: otp,
        couponCode: appliedCoupon?.trim() || undefined,
        couponApplied: !!appliedCoupon?.trim(),
        registrationPlatform: REGISTRATION_PLATFORM_MCARE,
        shareCode: referralCode.length > 3 ? referralCode : undefined,
        msisdn:
          appliedCoupon?.trim() && subscriptionMsisdn?.trim()
            ? subscriptionMsisdn.trim()
            : undefined,
      });

      await DataProvider.setToken(response.token);
      await DataProvider.setRefreshToken(response.refreshToken);
      setToken({ token: response.token });
      resetProfileCompletionGate();

      const newUsr = await DataProvider.updateProfile({
        fullname: name,
        email: emailLower,
        referralCode: referralCode.length > 3 ? referralCode : null,
      });
      setUser({ ...newUsr.data, timenow: new Date() });

      const tracking = localStorage.getItem("@tracking");
      if (tracking) {
        const payload = JSON.parse(tracking);
        const { data } = await DataProvider.updateTracking({
          source: payload.source,
          campaign: payload.campaign,
          trackingId: payload.trackingId,
          userId: newUsr.data.id,
        });
        if (data) {
          localStorage.setItem("@tracking", JSON.stringify(data));
        }
      }

      if (
        referralCode.toLocaleLowerCase() === "yellocare" ||
        referralCode.toLocaleLowerCase() === "prestige"
      ) {
        setActivateData({
          msisdn: "",
          name: name,
          yelloCareCode: "",
        });
        setLoading(false);
        navigate("/activate-with-yellocare");
        return;
      }

      if (response.subscriptionRequest) {
        if (response.subscriptionRequest.error) {
          openNotificationWithIcon(
            "warning",
            "Promo code",
            response.subscriptionRequest.statusMessage ||
              "Subscription request failed. You can subscribe from your dashboard.",
          );
        } else if (response.subscriptionRequest.packageLabel) {
          openNotificationWithIcon(
            "success",
            "Subscribed",
            `You've been subscribed to ${response.subscriptionRequest.packageLabel}. Check your dashboard for benefits.`,
          );
        }
        setPackage([]);
        setPlan(null);
        trackSignup("email", true);
        setLoading(false);
        navigate("/main");
        return;
      }

      trackSignup("email", true);
      setLoading(false);
      navigate("/pick-plan");
    } catch (error: any) {
      trackSignup("email", false);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to verify OTP. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    try {
      setLoading(true);
      const phoneNumberIntl = convertToInternationalFormat(phoneNumber);
      setActivateData({
        msisdn: phoneNumberIntl,
        name: name,
        yelloCareCode: phoneNumberIntl,
      });
      await AuthService.signUpPhone({
        phoneNumber: `+${phoneNumberIntl}`,
        fullname: name,
      });
      setIsOtpSent(true);
      setLoading(false);
      openNotificationWithIcon(
        "success",
        "OTP Sent",
        "Please check your phone for the verification code.",
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to send OTP. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length < 6) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Please enter the 6-digit OTP!",
      );
    }
    if (hasCouponEnteredButNotApplied()) {
      return openNotificationWithIcon(
        "warning",
        "Coupon not applied",
        "You entered a coupon code but did not apply it. Please apply the coupon or clear the field to continue.",
      );
    }
    setLoading(true);
    try {
      const phoneNumberIntl = convertToInternationalFormat(phoneNumber);

      const response = await AuthService.signUpVerify({
        phoneNumber: `+${phoneNumberIntl}`,
        otpCode: otp,
        fullname: name,
        couponCode: appliedCoupon?.trim() || undefined,
        couponApplied: !!appliedCoupon?.trim(),
        registrationPlatform: REGISTRATION_PLATFORM_MCARE,
      });

      await DataProvider.setToken(response.token);
      await DataProvider.setRefreshToken(response.refreshToken);
      setToken({ token: response.token });
      resetProfileCompletionGate();

      // Create user profile in the database
      const { data: newUsr } = await DataProvider.updateProfile({
        fullname: name,
        phoneNumber: convertToLocalFormat(phoneNumber),
        referralCode: referralCode.length > 3 ? referralCode : null,
      });

      // Update the app state with the new user
      setUser({ ...newUsr, timenow: new Date() });

      // Handle any tracking information if needed
      const tracking = localStorage.getItem("@tracking");
      if (tracking) {
        const payload = JSON.parse(tracking);
        const { data } = await DataProvider.updateTracking({
          source: payload.source,
          campaign: payload.campaign,
          trackingId: payload.trackingId,
          userId: newUsr.id,
        });

        if (data) {
          localStorage.setItem("@tracking", JSON.stringify(data));
        }
      }

      if (
        referralCode.toLocaleLowerCase() === "yellocare" ||
        referralCode.toLocaleLowerCase() === "prestige"
      ) {
        const payload = {
          msisdn: convertToInternationalFormat(
            activateData?.msisdn || phoneNumber,
          ),
          name: activateData?.name || name,
          yelloCareCode: convertToInternationalFormat(
            activateData?.msisdn || phoneNumber,
          ),
        };

        await DataProvider.activateWithYelloCare(payload);
        setLoading(false);
        navigate("/main");
        return;
      }

      if (response.subscriptionRequest) {
        if (response.subscriptionRequest.error) {
          openNotificationWithIcon(
            "warning",
            "Promo code",
            response.subscriptionRequest.statusMessage ||
              "Subscription request failed. You can subscribe from your dashboard.",
          );
        } else if (response.subscriptionRequest.packageLabel) {
          openNotificationWithIcon(
            "success",
            "Subscribed",
            `You've been subscribed to ${response.subscriptionRequest.packageLabel}. Check your dashboard for benefits.`,
          );
        }
        setPackage([]);
        setPlan(null);
        trackSignup("phone", true);
        setLoading(false);
        navigate("/main");
        return;
      }

      trackSignup("phone", true);
      setLoading(false);
      navigate("/pick-plan");
    } catch (error: any) {
      trackSignup("phone", false);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to verify OTP. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  };

  // MTN's existing short links point HERE (/sign-up?source=pulse|prestige),
  // but the campaign experience is the Kobikuul chat (Hughes: nothing
  // redirects to a registration form). Forward campaign arrivals with the
  // full query string so source/campaign attribution survives. Every other
  // source (e.g. google ads) and plain sign-ups keep this form.
  if (campaignSource) {
    return <Navigate to={`/kobikuul${location.search}`} replace />;
  }

  const isCampaignLanding = Boolean(campaignSource);
  const leftPanelImage = campaignSource
    ? campaignImageFor(campaignSource)
    : img;

  const steps = [
    {
      title: "Create account",
      content: (
        <UserRegister
          onSubmit={onSubmit}
          setCurrent={setCurrent}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          email={email}
          setEmail={setEmail}
          name={name}
          setName={setName}
          password={password}
          setPassword={setPassword}
          loading={loading}
          setLoading={setLoading}
          setReferralCode={setReferralCode}
          referralCode={referralCode}
          couponInput={couponInput}
          setCouponInput={setCouponInput}
          appliedCoupon={appliedCoupon}
          setAppliedCoupon={setAppliedCoupon}
          subscriptionMsisdn={subscriptionMsisdn}
          setSubscriptionMsisdn={setSubscriptionMsisdn}
          error={error}
          setError={setError}
          signupMethod={signupMethod}
          setSignupMethod={setSignupMethod}
          handleSignupMethodChange={handleSignupMethodChange}
          otp={otp}
          setOtp={setOtp}
          sendOtp={signupMethod === "email" ? sendEmailOtp : sendOtp}
          isOtpSent={isOtpSent}
          setIsOtpSent={setIsOtpSent}
          yelloCareCode={yelloCareCode}
          setYelloCareCode={setYelloCareCode}
          prestige={prestige}
          setPrestige={setPrestige}
        />
      ),
    },
    {
      title: "Subscription",
      content: <SelectPlan />,
    },
    {
      title: "Billing",
      content: "Billing information",
    },
  ];

  return (
    <div className="w-full h-full min-h-screen overflow-x-hidden">
      {/* Mobile view — card moved up 4px via -mt-1 */}
      <div className="min-h-screen lg:hidden flex flex-col pt-24 items-center w-full bg-white px-4 sm:px-6">
        <div className="w-full max-w-md flex flex-col items-center pb-6 -mt-1">
          <div className="absolute top-6 left-0 right-0 flex justify-center items-center gap-2 px-4">
            <Link to="/" className="shrink-0" aria-label="Home">
              <img
                src={MTNLogoblack}
                alt="MTN"
                className="h-6 sm:h-8 w-auto rounded-l-lg"
              />
            </Link>
            <img
              src={medpharmalogo}
              alt="MedPharma"
              className="h-5 sm:h-8 w-auto rounded-l-lg shrink-0"
            />
            <img
              src={mcareblack}
              alt="mCare"
              className="h-3.5 sm:h-5 w-auto rounded-l-lg shrink-0"
            />
          </div>
          {isCampaignLanding && (
            <img
              src={leftPanelImage}
              alt={`${campaignSource} mCare`}
              className="w-full mb-4 h-auto object-contain"
            />
          )}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mt-2 mb-2 text-center text-gray-900">
            Create Your Account
          </h1>
          <div className="w-full flex flex-col justify-start items-center">
            <Steps setCurrent={setCurrent} current={current} items={steps} />
            <div className="w-full min-w-0">{steps[current].content}</div>
          </div>
        </div>
      </div>

      {/* Desktop view */}
      <div className="h-screen w-full lg:flex hidden justify-between items-stretch overflow-hidden">
        <div
          className={`h-full min-w-0 flex-1 max-w-[50%] flex flex-col relative shrink-0 ${
            isCampaignLanding
              ? "items-center justify-center bg-[#F5C518] overflow-y-auto"
              : "justify-end items-center pb-24 bg-center bg-cover bg-[#1A94ECB5] bg-blend-overlay overflow-hidden"
          }`}
          style={
            isCampaignLanding
              ? undefined
              : { backgroundImage: `url(${img})` }
          }
        >
          <Link to="/" className="absolute top-6 left-4 z-10" aria-label="Home">
            <img
              src={images}
              alt="mCare"
              className="h-12 md:h-20 w-auto rounded-l-lg"
            />
          </Link>
          {isCampaignLanding ? (
            <div className="flex flex-1 w-full min-h-0 items-center justify-center px-2 pt-20 pb-4">
              <img
                src={leftPanelImage}
                alt={`${campaignSource} mCare`}
                className="max-h-full max-w-full w-auto h-auto object-contain"
              />
            </div>
          ) : (
            <>
              <h1 className="text-4xl xl:text-5xl font-bold text-white text-center px-4">
                Your gateway to urgent care
              </h1>
              <p className="text-white font-semibold text-2xl xl:text-3xl my-2 text-center px-4">
                With just a single tap
              </p>
            </>
          )}
        </div>

        <div className="h-full min-w-0 flex-1 max-w-[50%] flex items-start justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50 p-6 lg:p-8 xl:p-12 overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-lg py-6 lg:py-8 -mt-1">
            {error && error.length > 0 && (
              <div className="mb-4 lg:mb-6 flex justify-center items-center bg-red-100 rounded-xl p-3 lg:p-4 border border-red-300 shadow-sm">
                <div style={{ fontSize: 14, color: Colors.deepgray }}>
                  {error}
                </div>
              </div>
            )}
            <div className="bg-white/95 backdrop-blur-sm shadow border-0 rounded-xl p-4 sm:p-6">
              <div className="mb-4 lg:mb-6">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 leading-tight">
                  Create Your Account
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">
                  Join us today and start your health journey
                </p>
              </div>
              <div className="w-full flex flex-col justify-start items-center min-w-0">
                <Steps
                  setCurrent={setCurrent}
                  current={current}
                  items={steps}
                />
                <div className="w-full mt-4 min-w-0">
                  {steps[current].content}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {contextHolder}
    </div>
  );
}
